import {SetState} from '@wandb/weave/common/util/types';
import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';

import {envIsLocal} from '../../config';
import {usePropsSelector} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import * as ViewActions from '../../state/views/actions';
import {makeRedoSelector, makeUndoSelector} from '../../state/views/selectors';
import {LoadableView} from '../../state/views/types';
import {useWorkspaceAction} from '../../state/workspaces/hooks';
import {getWorkspaceDisplayName} from '../../state/workspaces/namedWorkspaceActionsInternal';
import {
  copyToOwn,
  deleteWorkspaceViewAndLoad,
  loadNamedWorkspace,
  resetToDefaultSpec as resetToDefaultSpecAction,
  saveAsNewWorkspace as saveAsNewWorkspaceAction,
  saveWorkspaceChanges,
  undoUnsavedChanges,
} from '../../state/workspaces/namedWorkspacesActions';
import {
  NamedWorkspacePermission,
  useNwPermissions,
  viewerCanOnlyReadNw,
  viewerCanOnlyWriteToOwnPersonal,
  viewerCanWriteToNw,
} from '../../state/workspaces/nwPermissions';
import {
  NamedWorkspaceId,
  ViewID,
  ViewTypes,
} from '../../state/workspaces/types';
import {WorkspaceViews} from '../../state/workspaces/workspaceViewListSelector';
import {useIsAdminModeActiveAndIsNotLocalEnv} from '../../util/admin';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {NamedWorkspaceViewBarProps, NamedWorkspaceViewType} from './types';
import {getWorkspaceType} from './utils';

export type NamedWorkspaceModalDetails = {
  type: 'delete view' | 'save view';
};

export type ViewBarContextType = NamedWorkspaceViewBarProps &
  Partial<ReturnType<typeof useNamedWorkspaceActions>> & {
    sharedViews: WorkspaceViews;
    personalWorkspaces: WorkspaceViews;
    currentWorkspaceNwId: NamedWorkspaceId;
    currentWorkspaceViewRef: ViewID;
    currentWorkspaceType: ViewTypes; // TODO - better naming needs to happen here
    workspaceType: ReturnType<typeof getWorkspaceType>;
    currentView?: LoadableView;
    displayName: string;
    viewer: Viewer | undefined; // if viewer = null, then that indicates viewer is anonymouse
    dispatch: Dispatch<any>;
    onMenuItemClick: (cid: string, nwId: NamedWorkspaceId | 'legacy') => void;
    modalDetails?: NamedWorkspaceModalDetails;
    isModalOpen: boolean;
    shouldRevokeWritePerms: boolean;
  };

export const ViewBarContext = createContext<ViewBarContextType | undefined>(
  undefined
);

type ViewBarActionContextType = {
  setModalDetails: SetState<NamedWorkspaceModalDetails | undefined>;
  setIsModalOpen: SetState<boolean>;
};
export const ViewBarActionContext = createContext<
  ViewBarActionContextType | undefined
>({
  setModalDetails: () => {},
  setIsModalOpen: () => {},
});

type ViewBarProps = NamedWorkspaceViewBarProps & {
  sharedViews: WorkspaceViews;
  personalWorkspaces: WorkspaceViews;
  currentWorkspaceNwId: NamedWorkspaceId;
  currentWorkspaceViewRef: ViewID;
  currentWorkspaceType: ViewTypes;
  currentView?: LoadableView;
  viewType: NamedWorkspaceViewType;
  children: React.ReactNode;
};

export const ViewBarContextProvider = ({
  workspaceStateId,
  sharedViews,
  personalWorkspaces,
  currentWorkspaceNwId,
  currentWorkspaceViewRef,
  currentWorkspaceType,
  currentView,
  viewType,
  isProjectReadOnly,
  children,
}: ViewBarProps) => {
  const dispatch = useDispatch();
  const viewer = useViewer();

  const [modalDetails, setModalDetails] =
    useState<NamedWorkspaceModalDetails>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const workspaceType = getWorkspaceType(currentWorkspaceNwId, currentView);

  const onMenuItemClick = useWorkspaceAction(
    workspaceStateId,
    loadNamedWorkspace
  );
  const displayName = getWorkspaceDisplayName(currentView);

  const nwPermissionsResult = useNwPermissions();

  // wb admins using powers and instance admins viewing restricted projects should
  // have write actions disabled in overflow menu
  const wbAdminUsingPowers = useIsAdminModeActiveAndIsNotLocalEnv();
  const shouldRevokeWritePerms = !!(
    wbAdminUsingPowers ||
    (envIsLocal &&
      viewer?.admin &&
      viewerCanOnlyWriteToOwnPersonal(nwPermissionsResult.data))
  );

  // Keep this destructure because we want to add these in useMemo.
  // Also keep these actions in a function so we can take advantage of the
  // return type of the function in ViewBarContextType.
  const actions = useNamedWorkspaceActions({
    workspaceStateId,
    currentWorkspaceViewRef,
    dispatch,
  });

  const {
    renameWorkspace,
    copyToPersonalWorkspace,
    saveAsNewWorkspace,
    deleteViewAndLoad,
    saveView,
    clearChanges,
    undoWorkspaceAction,
    redoWorkspaceAction,
    resetToDefaultSpec,
  } = getNwActionsForPermissions({
    nwPermissions: nwPermissionsResult.data,
    shouldRevokeWritePerms,
    currentWorkspaceNwId,
    actions,
  });

  const value = useMemo(
    () => ({
      workspaceStateId,
      dispatch,
      viewer,
      viewType,
      sharedViews,
      personalWorkspaces,
      currentWorkspaceNwId,
      currentWorkspaceViewRef,
      currentWorkspaceType,
      onMenuItemClick,
      workspaceType,
      currentView,
      displayName,
      renameWorkspace,
      copyToPersonalWorkspace,
      saveAsNewWorkspace,
      deleteViewAndLoad,
      saveView,
      clearChanges,
      undoWorkspaceAction,
      redoWorkspaceAction,
      resetToDefaultSpec,
      modalDetails,
      isModalOpen,
      isProjectReadOnly,
      shouldRevokeWritePerms,
    }),
    [
      workspaceStateId,
      dispatch,
      viewer,
      viewType,
      sharedViews,
      personalWorkspaces,
      currentWorkspaceNwId,
      currentWorkspaceViewRef,
      currentWorkspaceType,
      onMenuItemClick,
      workspaceType,
      currentView,
      displayName,
      renameWorkspace,
      copyToPersonalWorkspace,
      saveAsNewWorkspace,
      deleteViewAndLoad,
      saveView,
      clearChanges,
      undoWorkspaceAction,
      redoWorkspaceAction,
      resetToDefaultSpec,
      modalDetails,
      isModalOpen,
      isProjectReadOnly,
      shouldRevokeWritePerms,
    ]
  );

  const actionValue = useMemo(
    () => ({setModalDetails, setIsModalOpen}),
    [setModalDetails, setIsModalOpen]
  );

  return (
    <ViewBarContext.Provider value={value}>
      <ViewBarActionContext.Provider value={actionValue}>
        {children}
      </ViewBarActionContext.Provider>
    </ViewBarContext.Provider>
  );
};

export const useViewBarContext = (): ViewBarContextType => {
  const value = useContext(ViewBarContext);

  if (value == null) {
    throw new Error(
      'useViewBarContext must be used within a ViewBarContextProvider'
    );
  }

  return value;
};

export const useViewBarActionContext = (): ViewBarActionContextType => {
  const value = useContext(ViewBarActionContext);

  if (value == null) {
    throw new Error(
      'useViewBarActionContext must be used within a ViewBarActionContext'
    );
  }

  return value;
};

function useNamedWorkspaceActions({
  workspaceStateId,
  currentWorkspaceViewRef,
  dispatch,
}: {
  workspaceStateId: string;
  currentWorkspaceViewRef: ViewID;
  dispatch: Dispatch<any>;
}) {
  const {closeWorkspaceDrawer} = useWorkspaceDrawerContext();

  const renameWorkspace = useCallback(
    (newName: string) =>
      dispatch(ViewActions.rename(currentWorkspaceViewRef, newName)),
    [currentWorkspaceViewRef, dispatch]
  );

  const copyToPersonalWorkspace = useWorkspaceAction(
    workspaceStateId,
    copyToOwn
  );

  const saveAsNewWorkspace = useWorkspaceAction(
    workspaceStateId,
    saveAsNewWorkspaceAction
  );

  const deleteViewAndLoad = useWorkspaceAction(
    workspaceStateId,
    deleteWorkspaceViewAndLoad
  );

  const saveView = useWorkspaceAction(workspaceStateId, saveWorkspaceChanges);

  // Factory reset to view.defaultSpec
  const resetToDefaultSpec = useWorkspaceAction(
    workspaceStateId,
    resetToDefaultSpecAction
  );
  const resetWorkspaceAndCloseDrawer = useCallback(() => {
    resetToDefaultSpec();
    closeWorkspaceDrawer();
  }, [resetToDefaultSpec, closeWorkspaceDrawer]);

  const clearChanges = useWorkspaceAction(workspaceStateId, undoUnsavedChanges); // Undo all unsaved changes
  const undo = useCallback(() => dispatch(ViewActions.undo()), [dispatch]);
  const redo = useCallback(() => dispatch(ViewActions.redo()), [dispatch]);
  const undoActions = usePropsSelector(makeUndoSelector);
  const redoActions = usePropsSelector(makeRedoSelector);

  // We don't want to show warning or allow saving if user is just trying to rename
  const filteredUndoActions = undoActions.filter(
    action => action.type !== '@view/rename'
  );

  return {
    renameWorkspace,
    copyToPersonalWorkspace,
    saveAsNewWorkspace,
    deleteViewAndLoad,
    saveView: filteredUndoActions.length === 0 ? undefined : saveView,
    clearChanges: filteredUndoActions.length === 0 ? undefined : clearChanges,
    undoWorkspaceAction: filteredUndoActions.length === 0 ? undefined : undo,
    redoWorkspaceAction: redoActions.length === 0 ? undefined : redo,
    resetToDefaultSpec: resetWorkspaceAndCloseDrawer,
  };
}

export const DEFAULT_EMPTY_NW_ACTIONS = {
  renameWorkspace: undefined,
  copyToPersonalWorkspace: undefined,
  saveAsNewWorkspace: undefined,
  deleteViewAndLoad: undefined,
  saveView: undefined,
  clearChanges: undefined,
  undoWorkspaceAction: undefined,
  redoWorkspaceAction: undefined,
  resetToDefaultSpec: undefined,
};
export type NwActionsArgs = {
  nwPermissions: NamedWorkspacePermission[];
  shouldRevokeWritePerms: boolean;
  currentWorkspaceNwId: NamedWorkspaceId;
  actions: ReturnType<typeof useNamedWorkspaceActions>;
};
// Need to be explicit setting all fields to undefined, so they can be destructured
export function getNwActionsForPermissions({
  nwPermissions,
  shouldRevokeWritePerms,
  currentWorkspaceNwId,
  actions,
}: NwActionsArgs) {
  if (shouldRevokeWritePerms) {
    return {
      ...DEFAULT_EMPTY_NW_ACTIONS,
      copyToPersonalWorkspace: actions.copyToPersonalWorkspace,
      undoWorkspaceAction: actions.undoWorkspaceAction,
      redoWorkspaceAction: actions.redoWorkspaceAction,
      clearChanges: actions.clearChanges,
      resetToDefaultSpec: actions.resetToDefaultSpec,
    };
  }

  if (viewerCanWriteToNw(nwPermissions)) {
    return {
      ...actions,
      renameWorkspace:
        currentWorkspaceNwId.nwType === 'personal workspace'
          ? undefined
          : actions.renameWorkspace,
    };
  }

  if (viewerCanOnlyReadNw(nwPermissions)) {
    return {
      ...DEFAULT_EMPTY_NW_ACTIONS,
      undoWorkspaceAction: actions.undoWorkspaceAction,
      redoWorkspaceAction: actions.redoWorkspaceAction,
      clearChanges: actions.clearChanges,
    };
  }

  return DEFAULT_EMPTY_NW_ACTIONS;
}
